import {
    ApprovedIcon,
    ArchiveIcon,
    ChevronDownIcon,
    CompletedIcon,
    DesignPendingIcon,
    DesignedIcon,
    HoldIcon,
    InProgressIcon,
    ScheduledIcon,
    SubmittedIcon
} from '../icons';
import {
    Box,
    ListItemIcon,
    alpha,
    useTheme
} from '@mui/material';
import {
    IssueStatus,
    PermissionType
} from '../../models';
import {
    getUpdateIssueStatusCallback,
    issueStatusForIssueIdAtomFamily
} from '../../lib/design/document/state/DocumentState';
import {
    useMemo,
    useRef,
    useState
} from 'react';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import { PermissionResourceType } from '../../models';
import { PermissionRestrictedButton } from '../general/button/PermissionRestrictedButton';
import Popper from '@mui/material/Popper';
import { issueStatusText } from '../../componentsV2/issue/issuesStatusText';
import { useRecoilValue } from 'recoil';

type StatusDetails = {
    icon: JSX.Element;
    text: string;
    color: any;
};

const STATUS_TO_DETAIL_MAP = new Map<IssueStatus, StatusDetails>(
    [
        [
            IssueStatus.SUBMITTED,
            {
                icon: <SubmittedIcon primary="inherit" accent="inherit" />,
                text: issueStatusText[IssueStatus.SUBMITTED],
                color: "#999999"
            }
        ],
        [
            IssueStatus.ARCHIVED,
            {
                icon: <ArchiveIcon primary="inherit" accent="inherit" />,
                text: issueStatusText[IssueStatus.ARCHIVED],
                color: "#999999"
            }
        ],
        [
            IssueStatus.APPROVED,
            {
                icon: <ApprovedIcon primary="inherit" accent="inherit" />,
                text: issueStatusText[IssueStatus.APPROVED],
                color: "#388e3c"
            }
        ],
        [
            IssueStatus.IN_PROGRESS,
            {
                icon: <InProgressIcon primary="inherit" accent="inherit" />,
                text: issueStatusText[IssueStatus.IN_PROGRESS],
                color: "#03A9F4"
            }
        ],
        [
            IssueStatus.DESIGN_PENDING,
            {
                icon: <DesignPendingIcon primary="inherit" accent="inherit" />,
                text: issueStatusText[IssueStatus.DESIGN_PENDING],
                color: "#FFC107"
            }
        ],
        [
            IssueStatus.DESIGNED,
            {
                icon: <DesignedIcon primary="inherit" accent="inherit" />,
                text: issueStatusText[IssueStatus.DESIGNED],
                color: "#388E3C"
            }
        ],
        [
            IssueStatus.SCHEDULED,
            {
                icon: <ScheduledIcon primary="inherit" accent="inherit" />,
                text: issueStatusText[IssueStatus.SCHEDULED],
                color: "#2196F3"
            }
        ],
        [
            IssueStatus.ON_HOLD,
            {
                icon: <HoldIcon primary="inherit" accent="inherit" />,
                text: issueStatusText[IssueStatus.ON_HOLD],
                color: "#F6DA51"
            }
        ],
        [
            IssueStatus.COMPLETED,
            {
                icon: <CompletedIcon primary="inherit" accent="inherit" />,
                text: issueStatusText[IssueStatus.COMPLETED],
                color: "#388e3c"
            }
        ]
    ]
);

interface IssueStatusSelectMenuProps {
    readonly issueId: string;
    readonly size: "small" | "medium" | "large";
}

export const IssueStatusSelectMenu = ({ issueId, size }: IssueStatusSelectMenuProps) => {
    /* Internal Page State */
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const issueStatus: IssueStatus = useRecoilValue<IssueStatus>(issueStatusForIssueIdAtomFamily(issueId));

    /* Dependencies */
    const theme = useTheme();
    const updateIssueStatus = getUpdateIssueStatusCallback();

    const buttonDetails: StatusDetails = useMemo(() => {
        return STATUS_TO_DETAIL_MAP.get(issueStatus)!;
    }, [issueStatus]);

    const handleMenuItemClick = (status: IssueStatus) => {
        updateIssueStatus(issueId, status);
        setIsOpen(false);
    };

    return (
        <>
            <Box
                ref={anchorRef}
            >
                <PermissionRestrictedButton
                    size={size}
                    onClick={() => setIsOpen(true)}
                    startIcon={buttonDetails.icon}
                    sx={{
                        color: buttonDetails.color,
                        borderColor: buttonDetails.color,
                        backgroundColor: "transparent",
                        "&:hover": {
                            color: alpha(buttonDetails.color, 0.9),
                            borderColor: buttonDetails.color,
                        }
                    }}
                    resourceId={issueId}
                    requiredPermission={PermissionType.EDIT} //Issue status is part of the issue record, so it must be restricted by EDIT permission and not MANAGE_STATE
                    resourceType={PermissionResourceType.ISSUE}
                >
                    {buttonDetails.text}
                    <ChevronDownIcon />
                </PermissionRestrictedButton>
            </Box>
            <Popper
                style={{
                    zIndex: 2 // Force it to be displayed "on top" of the text field labels.
                }}
                open={isOpen}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === "bottom" ? "center top" : "center bottom"
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                                <MenuList id="split-button-menu" autoFocusItem>
                                    {Array.from(STATUS_TO_DETAIL_MAP.entries()).map(([status, details]) => (
                                        <MenuItem
                                            key={status}
                                            selected={status === issueStatus}
                                            onClick={() => handleMenuItemClick(status)}
                                        >
                                            <ListItemIcon>
                                                {details.icon}
                                            </ListItemIcon>
                                            {details.text}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

IssueStatusSelectMenu.defaultProps = {
    size: "medium"
};
